import React, { useEffect, useState } from "react";
import { Button, Descriptions, Divider, Modal, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { formatInZone } from "../../../utils/time";
import {
	getFinanceCustomer,
	createFinanceCustomer,
	deleteFinanceCustomer,
} from "../../../api/finance/FinanceApi";
import { StripeCustomer } from "../../../types/finance/FinanceTypes";
import PaymentMethodList from "../../../components/finance/PaymentMethodList";
import AddPaymentMethodModal from "../../../components/finance/AddPaymentMethodModal";
import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import { useNotifications } from "../../../context/NotificationContext";
import type { UserUuid } from "../../../types/identity";

/**
 * CU-86d44j2ac — Stripe payment information through the same generic finance API and payment-method
 * components the patient billing tab uses; nothing clinical is rendered.
 */
interface CustomerStripeSectionProps {
	userId: UserUuid;
	email?: string;
	name?: string;
	phone?: string | null;
	stripeCustomerId?: string | null;
	onStripeCustomerIdChange?: (stripeCustomerId: string | undefined) => void;
}

const CustomerStripeSection: React.FC<CustomerStripeSectionProps> = ({
	userId,
	email,
	name,
	phone,
	stripeCustomerId,
	onStripeCustomerIdChange,
}) => {
	const [stripeCustomer, setStripeCustomer] = useState<StripeCustomer | null>(null);
	const [loading, setLoading] = useState(false);
	const [addPaymentMethodVisible, setAddPaymentMethodVisible] = useState(false);
	const [refreshPaymentMethods, setRefreshPaymentMethods] = useState(0);
	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			if (!stripeCustomerId) {
				setStripeCustomer(null);
				return;
			}

			setLoading(true);
			try {
				const customer = await getFinanceCustomer(userId);
				if (!cancelled) setStripeCustomer(customer.stripe_customer);
			} catch (error: any) {
				if (!cancelled && error?.response?.status !== 404) {
					showError("Failed to load Stripe customer information");
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [userId, stripeCustomerId]);

	const handleCreate = async () => {
		try {
			const customer = await createFinanceCustomer({
				userId: String(userId),
				email: email ?? "",
				name: name ?? "",
				phone: phone ?? undefined,
			});
			setStripeCustomer(customer.stripe_customer);
			onStripeCustomerIdChange?.(customer.stripe_customer?.id);
			showSuccess("Stripe customer created successfully");
		} catch {
			showError("Failed to create Stripe customer");
		}
	};

	const handleDelete = () => {
		if (!stripeCustomer) return;

		Modal.confirm({
			title: "Delete Stripe Customer",
			content: "Are you sure you want to delete this Stripe customer? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteFinanceCustomer(userId);
					setStripeCustomer(null);
					onStripeCustomerIdChange?.(undefined);
					showSuccess("Stripe customer deleted");
				} catch {
					showError("Failed to delete Stripe customer");
				}
			},
		});
	};

	return (
		<>
			<SectionCard title="Stripe Payment Information">
				{loading ? (
					<InlineLoading message="Loading Stripe customer…" minHeight={120} />
				) : stripeCustomer ? (
					<div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
							<Typography.Text strong>Stripe Customer Details</Typography.Text>
							<Button danger size="small" icon={<DeleteOutlined />} onClick={handleDelete}>
								Delete Customer
							</Button>
						</div>
						<Descriptions bordered size="small" column={2}>
							<Descriptions.Item label="Customer ID" span={2}>{stripeCustomer.id}</Descriptions.Item>
							<Descriptions.Item label="Email" span={2}>{stripeCustomer.email}</Descriptions.Item>
							{stripeCustomer.name && <Descriptions.Item label="Name" span={2}>{stripeCustomer.name}</Descriptions.Item>}
							{stripeCustomer.phone && <Descriptions.Item label="Phone" span={2}>{stripeCustomer.phone}</Descriptions.Item>}
							<Descriptions.Item label="Created">
								{stripeCustomer.created ? formatInZone(stripeCustomer.created * 1000, "DD/MM/YYYY HH:mm") || "N/A" : "N/A"}
							</Descriptions.Item>
						</Descriptions>
						<Divider style={{ margin: "8px 0" }} />
						<PaymentMethodList
							customerId={stripeCustomer.id}
							onAddNew={() => setAddPaymentMethodVisible(true)}
							refreshTrigger={refreshPaymentMethods}
						/>
					</div>
				) : (
					<EmptyState
						title="No Stripe customer found for this customer"
						action={{ label: "Create Stripe Customer", onClick: handleCreate }}
					/>
				)}
			</SectionCard>

			{stripeCustomer && (
				<AddPaymentMethodModal
					visible={addPaymentMethodVisible}
					onClose={() => setAddPaymentMethodVisible(false)}
					onSuccess={() => setRefreshPaymentMethods((prev) => prev + 1)}
					customerId={stripeCustomer.id}
				/>
			)}
		</>
	);
};

export default CustomerStripeSection;
